import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Layers } from "lucide-react";

const REQ_TYPES = [
  { v: "",              label: "— Keep existing —" },
  { v: "mandatory",     label: "Mandatory" },
  { v: "role_required", label: "Role Required" },
  { v: "recommended",   label: "Recommended" },
  { v: "optional",      label: "Optional" },
];

const ROLES = ["care_worker", "manager", "deputy_manager", "service_manager", "registered_manager", "admin"];
const SERVICE_TYPES = ["supported_living", "residential_care", "domiciliary_care", "nursing_care"];

/**
 * Bulk-assign dialog — ticks a set of courses, picks a set of roles/sites/
 * service_types (and optionally a new requirement_type), then applies in one
 * call. `mode=merge` unions with existing values; `mode=replace` overwrites.
 */
export default function BulkAssignDialog({ open, onOpenChange, onSaved }) {
  const [courses, setCourses] = useState([]);
  const [sites, setSites] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [requirement, setRequirement] = useState("");
  const [roles, setRoles] = useState([]);
  const [siteIds, setSiteIds] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [mode, setMode] = useState("merge");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set()); setRequirement(""); setRoles([]); setSiteIds([]); setServiceTypes([]); setMode("merge");
    api.get("/training/courses", { params: { active_only: true } }).then((r) => setCourses(r.data.items || [])).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data || [])).catch(() => {});
  }, [open]);

  const toggle = (setter, list, value) => setter(list.includes(value) ? list.filter((x) => x !== value) : [...list, value]);
  const toggleCourse = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(courses.map((c) => c.id)));
  const clearAll = () => setSelected(new Set());

  const apply = async () => {
    if (selected.size === 0) return toast.error("Pick at least one course");
    if (!requirement && roles.length === 0 && siteIds.length === 0 && serviceTypes.length === 0) {
      return toast.error("Pick at least one change to apply");
    }
    if (requirement === "role_required" && roles.length === 0) {
      return toast.error("Role Required needs at least one role selected");
    }
    setSaving(true);
    try {
      const body = { course_ids: Array.from(selected), mode };
      if (requirement) body.requirement_type = requirement;
      if (roles.length) body.roles = roles;
      if (siteIds.length) body.site_ids = siteIds;
      if (serviceTypes.length) body.service_types = serviceTypes;
      const r = await api.post("/training/courses/bulk-assign", body);
      toast.success(`Applied to ${r.data.updated} course${r.data.updated === 1 ? "" : "s"}`);
      onSaved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const grouped = groupByCategory(courses);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto" data-testid="bulk-assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Layers className="w-4 h-4 text-sky-700" /> Bulk Assign Requirements</DialogTitle>
          <DialogDescription>Pick several courses, then apply a role, site or service type in one action.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          {/* Left — course picker */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Courses ({selected.size} selected)</div>
              <div className="flex gap-2 text-[11px]">
                <button onClick={selectAll} className="text-sky-700 hover:underline" data-testid="btn-bulk-select-all">Select all</button>
                <button onClick={clearAll} className="text-slate-500 hover:underline" data-testid="btn-bulk-clear">Clear</button>
              </div>
            </div>
            <div className="border border-slate-200 rounded-lg max-h-[420px] overflow-y-auto p-2 bg-white" data-testid="bulk-assign-courses">
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} className="mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold px-1 py-1">{cat}</div>
                  {items.map((c) => (
                    <label key={c.id} className="flex items-center gap-2 py-1 px-1 rounded hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={selected.has(c.id)} onChange={() => toggleCourse(c.id)} data-testid={`bulk-course-${c.id}`} />
                      <span className="text-sm text-slate-800 flex-1 min-w-0 truncate">{c.name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 capitalize">{(c.requirement_type || "").replace(/_/g, " ")}</span>
                    </label>
                  ))}
                </div>
              ))}
              {courses.length === 0 && <div className="p-6 text-sm text-slate-500 text-center">No active courses.</div>}
            </div>
          </div>

          {/* Right — targets */}
          <div className="space-y-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Change requirement type (optional)</div>
              <select value={requirement} onChange={(e) => setRequirement(e.target.value)} data-testid="bulk-req-type" className="h-9 w-full text-sm border border-slate-300 rounded-md bg-white px-2">
                {REQ_TYPES.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Apply to roles</div>
              <div className="flex flex-wrap gap-2">
                {ROLES.map((r) => (
                  <label key={r} className="flex items-center gap-1 text-xs text-slate-700 capitalize">
                    <input type="checkbox" checked={roles.includes(r)} onChange={() => toggle(setRoles, roles, r)} data-testid={`bulk-role-${r}`} />
                    {r.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Apply to sites</div>
              <div className="flex flex-wrap gap-2">
                {sites.map((s) => (
                  <label key={s.id} className="flex items-center gap-1 text-xs text-slate-700">
                    <input type="checkbox" checked={siteIds.includes(s.id)} onChange={() => toggle(setSiteIds, siteIds, s.id)} data-testid={`bulk-site-${s.id}`} />
                    {s.name}
                  </label>
                ))}
                {sites.length === 0 && <span className="text-[11px] text-slate-500">No sites.</span>}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Apply to service types</div>
              <div className="flex flex-wrap gap-2">
                {SERVICE_TYPES.map((s) => (
                  <label key={s} className="flex items-center gap-1 text-xs text-slate-700 capitalize">
                    <input type="checkbox" checked={serviceTypes.includes(s)} onChange={() => toggle(setServiceTypes, serviceTypes, s)} data-testid={`bulk-service-${s}`} />
                    {s.replace(/_/g, " ")}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Merge with existing or replace</div>
              <div className="flex gap-4 text-sm text-slate-800">
                <label className="flex items-center gap-1"><input type="radio" name="bulk-mode" checked={mode === "merge"} onChange={() => setMode("merge")} data-testid="bulk-mode-merge" /> Merge (union)</label>
                <label className="flex items-center gap-1"><input type="radio" name="bulk-mode" checked={mode === "replace"} onChange={() => setMode("replace")} data-testid="bulk-mode-replace" /> Replace</label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-200 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-bulk-cancel">Cancel</Button>
          <Button onClick={apply} disabled={saving} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-bulk-apply">
            {saving ? "Applying…" : `Apply to ${selected.size} course${selected.size === 1 ? "" : "s"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function groupByCategory(courses) {
  const g = {};
  for (const c of courses) {
    const cat = c.category || "General";
    (g[cat] ||= []).push(c);
  }
  return g;
}
